import { usePreferences } from '../context/PreferencesContext';
import { useTranslation } from 'react-i18next';
import React, { useId, useRef, useState } from 'react';
import '../css/ProcessModal.css';
import {
  initiateTreatmentPlanLetter,
  initiateReferralLetter,
  initiateConsultationLetter,
  initiateSoapNote,
  initiateDischargeSummary,
  initiateSpecialtyTemplate
} from '../utils/ProcessFunctionUtils';

function ProcessModal({ onClose, noteContent, sessionId, onProcessStart, onProcessComplete, onProcessError }) {
  const { t } = useTranslation();
  const { ready } = usePreferences();
  const [additionalInformation, setAdditionalInformation] = useState('');
  const submitted = useRef(false);
  const titleId = useId();
  const canGenerate = ready && typeof noteContent === 'string' && noteContent.trim() !== '';

  const handleGenerateClick = (documentType, processingFunction) => {
    if (!canGenerate || submitted.current) return;
    submitted.current = true;
    const jobId = onProcessStart(documentType);
    onClose();

    Promise.resolve()
      .then(() => processingFunction(noteContent, additionalInformation, sessionId))
      .then(response => {
        onProcessComplete(jobId, response);
      })
      .catch(() => {
        onProcessError(jobId);
      });
  };

  return (
    <div className="process-modal-overlay" onClick={onClose}>
      <div className="process-modal-content" role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 id={titleId}>{t('process.title')}</h2>
          <button onClick={onClose} className="close-button-process-modal">{t('common.close')}</button>
        </div>

        <div className="modal-body">
          <div className="modal-left">
            <p>{t('process.help')}</p>

            <div className="letter-section">
              <h3>{t('process.letters')}</h3>
              <button
                className="modal-button referral-letter-button"
                disabled={!canGenerate}
                onClick={() => handleGenerateClick('referral_letter', initiateReferralLetter)}
              >{t('documents.referral_letter')}</button>
              <button
                className="modal-button follow-up-letter-button"
                disabled={!canGenerate}
                onClick={() => handleGenerateClick('treatment_plan_letter', initiateTreatmentPlanLetter)}
              >{t('process.treatmentLabel')}</button>
              <button
                className="modal-button consultation-letter-button"
                disabled={!canGenerate}
                onClick={() => handleGenerateClick('consultation_letter', initiateConsultationLetter)}
              >{t('documents.consultation_letter')}</button>
            </div>

            <div className="template-section">
              <h3>{t('process.conversion')}</h3>
              <button
                className="modal-button soap-template-button"
                disabled={!canGenerate}
                onClick={() => handleGenerateClick('soap_note', initiateSoapNote)}
              >{t('documents.soap_note')}</button>
              <button
                className="modal-button discharge-template-button"
                disabled={!canGenerate}
                onClick={() => handleGenerateClick('discharge_summary', initiateDischargeSummary)}
              >{t('documents.discharge_summary')}</button>
              <button
                className="modal-button specialty-template-button"
                disabled={!canGenerate}
                onClick={() => handleGenerateClick('specialty_template', initiateSpecialtyTemplate)}
              >{t('process.specialtyLabel')}</button>
            </div>
          </div>

          <div className="modal-right">
            <textarea dir="auto"
              value={noteContent}
              readOnly
              aria-label={t('notes.viewer')}
              className="modal-textarea"
            />
            <textarea dir="auto"
              value={additionalInformation}
              onChange={(e) => setAdditionalInformation(e.target.value)}
              placeholder={t('process.additionalInfo')}
              aria-label={t('process.additionalInfo')}
              className="additional-info-textarea"
              maxLength={1000}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProcessModal;
